import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Dumbbell, UtensilsCrossed, Heart, Trophy, Scale, Lightbulb, TrendingDown, TrendingUp, Minus, CalendarDays, Flame, Footprints, Target } from 'lucide-react';
import { format } from 'date-fns';

const pctColor = (v) => v == null ? '#7E8C9E' : v >= 85 ? '#65A30D' : v >= 60 ? '#FFB800' : '#FF5C5C';

// Week-over-week trend arrow. `goodWhenDown` for weight (losing = improving).
export function TrendArrow({ delta, unit = '%', goodWhenDown = false, size = 'sm' }) {
  if (delta == null) return <span className="text-[10px] text-muted-foreground">—</span>;
  const stable = Math.abs(delta) < (unit === 'kg' ? 0.1 : 1);
  const improving = stable ? false : goodWhenDown ? delta < 0 : delta > 0;
  const color = stable ? '#FFB800' : improving ? '#65A30D' : '#FF5C5C';
  const Icon = stable ? Minus : (delta > 0 ? TrendingUp : TrendingDown);
  const cls = size === 'lg' ? 'text-sm' : 'text-[11px]';
  const ic = size === 'lg' ? 'w-3.5 h-3.5' : 'w-3 h-3';
  const val = unit === 'kg' ? `${delta > 0 ? '+' : ''}${delta.toFixed(1)}` : `${delta > 0 ? '+' : ''}${delta}`;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono font-bold ${cls}`} style={{ color }} data-testid="trend-arrow">
      <Icon className={ic} /> {val}{unit === '%' ? '%' : unit === 'kg' ? ' kg' : ''}
    </span>
  );
}

export function WeeklyDigest({ clientId, showToggle = true }) {
  const [d, setD] = useState(null);
  const [offset, setOffset] = useState(1); // default: last completed week (the Monday report)
  const [history, setHistory] = useState([]);
  useEffect(() => {
    const params = clientId ? { client_id: clientId } : {};
    api.get('/weekly-reports', { params }).then(r => setHistory(r.data || [])).catch(() => {});
  }, [clientId]);
  useEffect(() => {
    setD(null);
    const params = { week_offset: offset, ...(clientId ? { client_id: clientId } : {}) };
    api.get('/weekly-digest', { params }).then(r => setD(r.data));
  }, [clientId, offset]);

  const mondayNow = (() => { const t = new Date(); const day = (t.getDay() + 6) % 7; t.setDate(t.getDate() - day); t.setHours(0, 0, 0, 0); return t; })();
  const opts = [{ v: 0, l: 'This Week' }, { v: 1, l: 'Last Week' }];
  history.forEach((h) => {
    const ws = new Date(h.week_start + 'T00:00:00');
    const off = Math.round((mondayNow - ws) / (7 * 86400000));
    if (off >= 2 && off <= 12 && !opts.some(o => o.v === off)) opts.push({ v: off, l: `Wk of ${h.week_start.slice(5)}` });
  });
  opts.sort((a, b) => a.v - b.v);

  return (
    <div className="space-y-3" data-testid="weekly-digest-wrap">
      {showToggle && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="digest-week-toggle">
          {opts.map(o => (
            <button key={o.v} onClick={() => setOffset(o.v)} data-testid={`digest-week-${o.v}`}
              className={`shrink-0 px-3.5 h-9 rounded-full text-xs font-semibold transition ${offset === o.v ? 'bg-[var(--c-green)] text-[#081726]' : 'bg-[var(--c-film)] text-muted-foreground'}`}>{o.l}</button>
          ))}
        </div>
      )}
      {!d ? <div className="text-muted-foreground text-sm">Loading weekly report…</div>
        : d.ready === false ? (
          <div className="regen-card-elevated p-8 text-center" data-testid="digest-empty">
            <CalendarDays className="w-8 h-8 mx-auto mb-3 text-[var(--c-green-ink)]" />
            <div className="font-display font-bold text-lg">Weekly Report</div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-xs mx-auto">Complete your first week to receive your personalized weekly performance report.</p>
          </div>
        ) : <DigestBody d={d} />}
    </div>
  );
}

function DigestBody({ d }) {
  const rng = `${format(new Date(d.week_start), 'MMM d')} – ${format(new Date(d.week_end), 'MMM d')}`;
  const tr = d.trends || {};
  const na = d.nutrition_avg || {};
  const st = d.steps || {};
  const cards = [
    { key: 'training', icon: Dumbbell, color: '#00C8FF', label: 'Training', pct: d.training.scored_days > 0 ? d.training.compliance : null,
      sub: d.training.scored_days > 0 ? `${d.training.completed}/${d.training.total} gym sessions · ${d.training.walk_days_hit}/${d.training.scored_days} brisk walks` : 'No training scheduled yet', delta: tr.training?.delta },
    { key: 'nutrition', icon: UtensilsCrossed, color: '#FFB800', label: 'Nutrition', pct: d.nutrition.has_target ? d.nutrition.compliance : null,
      sub: d.nutrition.has_target ? `${d.nutrition.actual} / ${d.nutrition.planned} kcal` : 'No target set', delta: tr.nutrition?.delta },
    { key: 'recovery', icon: Heart, color: '#A78BFA', label: 'Recovery', pct: d.recovery.score,
      sub: `Sleep ${d.recovery.sleep} · Fast ${d.recovery.fasting}`, delta: tr.recovery?.delta },
  ];

  return (
    <div className="space-y-4" data-testid="weekly-digest">
      <div className="regen-card-elevated p-5 blueprint-grid">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 label-mini text-[var(--c-green-ink)]"><CalendarDays className="w-3.5 h-3.5" /> Weekly Compliance Digest</div>
          <span className="text-xs text-muted-foreground">{rng}</span>
        </div>

        {/* YourReGen Score with trend */}
        <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-[var(--c-film)] px-4 py-2.5" data-testid="digest-regen">
          <Flame className="w-4 h-4 text-[#00C8FF]" />
          <span className="text-sm font-semibold">YourReGen Score</span>
          <span className="font-mono text-lg ml-auto" style={{ color: pctColor(d.regen_score) }}>{d.regen_score}</span>
          <TrendArrow delta={tr.regen?.delta} size="lg" />
        </div>

        {/* Compliance rings */}
        <div className="grid grid-cols-3 gap-3 mt-3">
          {cards.map(c => (
            <div key={c.key} className="rounded-2xl bg-[var(--c-film)] p-4 text-center" data-testid={`digest-${c.key}`}>
              <c.icon className="w-4 h-4 mx-auto" style={{ color: c.color }} />
              <div className="font-mono text-3xl mt-1.5" style={{ color: pctColor(c.pct) }}>{c.pct == null ? '—' : `${c.pct}%`}</div>
              <div className="text-xs font-semibold mt-0.5">{c.label}</div>
              <div className="mt-1"><TrendArrow delta={c.delta} /></div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{c.sub}</div>
            </div>
          ))}
        </div>

        {/* Nutrition detail */}
        {d.nutrition.has_target && (
          <div className="mt-3 text-[11px] text-muted-foreground" data-testid="digest-nutrition-detail">
            {d.nutrition.planned} planned + {d.nutrition.additional} additional = {d.nutrition.actual} actual kcal
          </div>
        )}

        {/* Weight change */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-[var(--c-film)] p-3.5" data-testid="digest-weight">
            <div className="label-mini flex items-center gap-1.5"><Scale className="w-3.5 h-3.5" /> Weekly Weight</div>
            {d.weight.diff != null ? (
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-lg">{d.weight.previous} → {d.weight.current} kg</span>
                <span className="font-mono text-sm font-bold inline-flex items-center gap-0.5" style={{ color: d.weight.diff <= 0 ? '#65A30D' : '#FFB800' }}>
                  {d.weight.diff <= 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
                  {d.weight.diff > 0 ? '+' : ''}{d.weight.diff} kg
                </span>
              </div>
            ) : <div className="text-sm text-muted-foreground mt-1">Not enough weight data</div>}
          </div>
          <div className="rounded-xl bg-[var(--c-green-soft)] p-3.5" data-testid="digest-bonus">
            <div className="label-mini flex items-center gap-1.5 text-[var(--c-green-ink)]"><Trophy className="w-3.5 h-3.5" /> Activity Bonus</div>
            <div className="mt-1 font-mono text-lg text-[var(--c-green-ink)]">+{d.bonus.sessions} · +{d.bonus.minutes} min</div>
            {d.bonus.activities.length > 0 && <div className="text-[10px] text-muted-foreground mt-0.5">{d.bonus.activities.join(' · ')}</div>}
          </div>
        </div>

        {/* Steps + average daily macros */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-[var(--c-film)] p-3.5" data-testid="digest-steps">
            <div className="label-mini flex items-center gap-1.5"><Footprints className="w-3.5 h-3.5" /> Steps</div>
            <div className="mt-1 font-mono text-lg">{(st.avg || 0).toLocaleString()}<span className="text-xs text-muted-foreground"> /day avg</span></div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{(st.total || 0).toLocaleString()} total this week</div>
          </div>
          <div className="rounded-xl bg-[var(--c-film)] p-3.5" data-testid="digest-macros-avg">
            <div className="label-mini flex items-center gap-1.5"><UtensilsCrossed className="w-3.5 h-3.5" /> Avg / day</div>
            <div className="mt-1 font-mono text-sm">{na.calories || 0} kcal</div>
            <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">P {na.protein || 0} · C {na.carbs || 0} · F {na.fat || 0}</div>
          </div>
        </div>

        {/* Coach insight */}
        <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-[#A78BFA]/10 p-3.5" data-testid="digest-insight">
          <Lightbulb className="w-4 h-4 text-[#A78BFA] mt-0.5 flex-shrink-0" />
          <p className="text-sm leading-relaxed text-foreground/90">{d.insight}</p>
        </div>
      </div>

      {/* Achievements */}
      {(d.achievements || []).length > 0 && (
        <div className="regen-card-elevated p-5" data-testid="digest-achievements">
          <div className="label-mini text-[var(--c-green-ink)] flex items-center gap-1.5 mb-3"><Trophy className="w-3.5 h-3.5" /> Achievements</div>
          <div className="grid grid-cols-1 gap-2">
            {d.achievements.map((a, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl bg-[var(--c-green-soft)] p-3" data-testid={`achievement-${i}`}>
                <Trophy className="w-5 h-5 text-[var(--c-green-ink)] flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold">{a.label}</div>
                  <div className="text-[11px] text-muted-foreground">{a.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next Week Focus */}
      {(d.next_week_focus || []).length > 0 && (
        <div className="regen-card-elevated p-5" data-testid="digest-focus">
          <div className="label-mini text-[#00C8FF] flex items-center gap-1.5 mb-3"><Target className="w-3.5 h-3.5" /> Next Week Focus</div>
          <div className="space-y-2">
            {d.next_week_focus.map((t, i) => (
              <div key={i} className="flex items-start gap-2.5 text-sm" data-testid={`focus-${i}`}>
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00C8FF] flex-shrink-0" />
                <span className="text-foreground/90">{t}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
